import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DropdownItem, InputType } from '@vinnova/ui';
import { CountryService, MinOptionsDropDown } from '@vinnova/validation';
import {
  BehaviorSubject,
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { FormQuestionFollowUp } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureOptions } from '../../../core/models/question/question-options.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { BaseWorksiteService } from '../../../core/services/base-worksite.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-dropdown',
  templateUrl: './question-dropdown.component.html',
  styleUrls: ['./question-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionDropdownComponent implements OnInit, OnChanges {
  value: FormControl<string>;

  @Input() labelPrefix: string;
  @Input() repeaterGroupId: string;

  InputType = InputType;

  items: WritableSignal<DropdownItem[]> = signal([]);
  readonlyItems: WritableSignal<string[]> = signal([]);
  questionNumber$: Observable<unknown>;
  readOnly$: Observable<ReadonlyState>;
  activeLang$: Observable<string>;
  errorMessage$: Observable<string>;
  helpText$: Observable<string>;
  questionChange = new Subject<void>();
  questionChange$ = this.questionChange.asObservable();
  placeholder: string;
  countryOption = new BehaviorSubject(false);
  countryOption$ = this.countryOption.asObservable();
  organisationOption = new BehaviorSubject(false);
  organisationOption$ = this.organisationOption.asObservable();
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  width = signal('medium');
  showChanges = input<boolean>();
  readonly previousAnswersService = inject(PreviousAnswersService);

  ReadonlyState = ReadonlyState;

  _questionForm: FormGroup<ControlsOf<FormQuestionFollowUp>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionFollowUp>>) {
    this._questionForm = form;
    this.value = this._questionForm.get('value');

    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }
  get questionForm() {
    return this._questionForm;
  }

  _question: Question;
  @Input() set question(value: Question) {
    this._question = value;
    if ((value.structure as QuestionStructureOptions).countryOption) {
      this.countryOption.next(true);
    }
    if ((value.structure as QuestionStructureOptions).organisationOption) {
      this.organisationOption.next(true);
    }
    const min = (value.structure as QuestionStructureOptions)
      .minNumberOfCheckedOptions;
    if (
      min &&
      value.validation.required &&
      (value.structure as QuestionStructureOptions).dropdownMultiple
    ) {
      this.value.addValidators(MinOptionsDropDown(min));
      this.value.updateValueAndValidity();
    }
    this.questionChange.next();
    this.helpText$ = this.createHelpText();
  }

  get question() {
    return this._question;
  }

  get structure() {
    return this.question.structure as QuestionStructureOptions;
  }

  get minNumberOfCheckedOptions() {
    return +this.structure.minNumberOfCheckedOptions;
  }

  get maxNumberOfCheckedOptions() {
    return +this.structure.maxNumberOfCheckedOptions;
  }

  constructor(
    private formValidationService: FormValidationService,
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private translocoService: TranslocoService,
    private questionNumberService: QuestionNumberService,
    private translatePipe: TranslatePipe,
    private formsQuery: RendererFormsQuery,
    private worksiteService: BaseWorksiteService,
  ) {}

  ngOnInit() {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;
    const countriesEn = CountryService.getAllCountries('en');
    const countriesSv = CountryService.getAllCountries('sv');

    merge(
      this.countryOption$,
      this.organisationOption$,
      this.activeLang$,
      this.questionChange$,
    )
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.structure.countryOption) {
          const lang = this.translocoService.getActiveLang();
          const placeholder = lang === 'sv' ? 'Välj land' : 'Choose country';
          this.placeholder = placeholder;
          this.items.set(lang === 'sv' ? countriesSv : countriesEn);
          this.setReadonly();
          this.width.set('medium');
        } else if (this.structure.organisationOption) {
          const lang = this.translocoService.getActiveLang();
          const placeholder =
            lang === 'sv' ? 'Välj organisation' : 'Choose organisation';
          this.placeholder = placeholder;
          this.width.set('full');
        }
      });

    this.worksiteService.worksites$
      .pipe(untilDestroyed(this))
      .subscribe((worksites) => {
        if (this.structure.organisationOption) {
          const worksiteItems = worksites.map((worksite) => ({
            value: worksite.id,
            label: worksite.address
              ? `${worksite.company.nationalRegistrationNumber} - ${worksite.name}, ${worksite.address}`
              : `${worksite.company.nationalRegistrationNumber} - ${worksite.name}`,
          }));
          this.items.set(worksiteItems);
          if (this.value.value) {
            const selectedIds = this.structure.dropdownMultiple
              ? JSON.parse(this.value.value)
              : [this.value.value];

            const validIds = selectedIds.filter((id) =>
              worksites.some((w) => w.id === id),
            );

            if (validIds.length !== selectedIds.length) {
              if (this.structure.dropdownMultiple) {
                this.value.patchValue(
                  validIds.length > 0 ? JSON.stringify(validIds) : undefined,
                );
              } else {
                this.value.patchValue(undefined);
              }
            }
          }

          this.setReadonly();
          this.cdr.markForCheck();
        }
      });

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );
    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );
    this.readOnly$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.setReadonly());
    this.setErrorMessage();

    const updateTextObs = merge(
      this.translocoService.langChanges$,
      this.questionChange$,
    );

    if (!this.structure.countryOption && !this.structure.organisationOption) {
      updateTextObs
        .pipe(
          map(() => {
            return (
              this.question.structure as QuestionStructureOptions
            ).options.map((option) => {
              return {
                value: option.value,
                label: this.translatePipe.transform(
                  this.question.translations?.list[option.value],
                  this.translocoService.getActiveLang(),
                  option.text,
                ),
              };
            });
          }),
          untilDestroyed(this),
        )
        .subscribe((items) => {
          this.items.set(items);
          this.setReadonly();
        });
    }

    if (this.items()?.some((i) => i.label?.length > 50)) {
      this.width.set('full');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          if (this.value.value === '[]') {
            this.value.patchValue(undefined);
          }
        });

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setReadonly());

      if (
        this.formsQuery.getReadOnly() &&
        !changes['repeaterGroupId'].firstChange
      ) {
        this.setReadonly();
      }
    }
  }

  setErrorMessage() {
    this.errorMessage$ = this.readOnly$.pipe(
      switchMap((readonly) => {
        switch (readonly) {
          case ReadonlyState.Editable: {
            return this.formValidationService
              .questionInvalid(this.value, this.question.id)
              .pipe(
                switchMap((invalid) => {
                  return invalid
                    ? this.value.errors.required && !this.minNumberCheck()
                      ? this.translocoService.selectTranslate('required')
                      : this.createHelpText()
                    : of(undefined);
                }),
              );
          }
          case ReadonlyState.Locked: {
            return this.value.errors?.required
              ? this.translocoService.selectTranslate('field_required')
              : of(undefined);
          }
          case ReadonlyState.Reader: {
            return of(undefined);
          }
        }
      }),
    );
  }

  createHelpText(): Observable<string> {
    if (this.maxNumberCheck() && this.minNumberCheck()) {
      if (this.maxNumberOfCheckedOptions === this.minNumberOfCheckedOptions) {
        return this.translocoService.selectTranslate('checkbox_exact', {
          minVal: this.minNumberOfCheckedOptions,
        });
      }
      return this.translocoService.selectTranslate(
        'checkboxes_min_and_max_NumberOfCheckedOptions',
        {
          minNumberOfCheckedOptions: this.minNumberOfCheckedOptions,
          maxNumberOfCheckedOptions: this.maxNumberOfCheckedOptions,
        },
      );
    } else if (this.minNumberCheck()) {
      return this.translocoService.selectTranslate('checkbox_min', {
        minVal: this.minNumberOfCheckedOptions,
      });
    } else if (this.maxNumberCheck()) {
      return this.maxNumberOfCheckedOptions > 1
        ? this.translocoService.selectTranslate('checkbox_max', {
            maxVal: this.maxNumberOfCheckedOptions,
          })
        : of(undefined);
    } else {
      return of(undefined);
    }
  }

  public getChoosenDropdowns(dropdowns: DropdownItem[]): string[] {
    const choosenDropdowns = [];
    for (const dropdown of dropdowns) {
      if (this.value.value?.includes(dropdown.value)) {
        choosenDropdowns.push(dropdown.label);
      }
    }
    return choosenDropdowns;
  }

  public maxNumberCheck() {
    return (
      this.maxNumberOfCheckedOptions && !isNaN(this.maxNumberOfCheckedOptions)
    );
  }

  private minNumberCheck() {
    return (
      this.minNumberOfCheckedOptions && !isNaN(this.minNumberOfCheckedOptions)
    );
  }

  private setReadonly() {
    if (this.formsQuery.getReadOnly()) {
      this.readonlyItems.set(this.getChoosenDropdowns(this.items()));
    }
  }
}
